import { groupRuleWarnings } from "@/dashboard/inventory_management/cancel/cancellationRuleMessages";
import type { CancellationRuleWarning } from "@/dashboard/inventory_management/cancel/cancellationRulesTypes";
import { useCancellationTargets } from "@/dashboard/inventory_management/cancel/useCancellationTargetsStore";
import { ruleWarningKey } from "@/dashboard/inventory_management/cancel/useCancelRuleEvaluation";
import type { IndividualCancelStatement } from "@/types";
import { groupBy } from "lodash-es";
import { type MaybeRefOrGetter, computed, toValue } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Groups the statements' rule warnings (`useCancelRuleEvaluation`) as shown.
 *
 * `groups` holds every warning once, naming each statement it reads the same for. `repeated` are the groups that
 * concern several statements; `byStatement` holds the rest under their statement's `ruleWarningKey`, for a caller
 * that shows those next to the statement.
 *
 * `shows` leaves out a statement's warnings the caller already says elsewhere.
 */
export const useStatementRuleWarningGroups = (options: {
  statements: MaybeRefOrGetter<IndividualCancelStatement[]>;
  warningsByKey: MaybeRefOrGetter<Record<string, CancellationRuleWarning[]>>;
  label: (statement: IndividualCancelStatement, index: number) => string;
  shows?: (warning: CancellationRuleWarning) => boolean;
}) => {
  const { t, locale } = useI18n();
  const targets = useCancellationTargets();

  const groups = computed(() => {
    const warningsByKey = toValue(options.warningsByKey);
    return groupRuleWarnings(
      toValue(options.statements).map((statement, index) => {
        const key = ruleWarningKey(statement, index);
        const warnings = warningsByKey[key] ?? [];
        return {
          key,
          label: options.label(statement, index),
          warnings: options.shows ? warnings.filter(options.shows) : warnings,
        };
      }),
      { t, locale: locale.value, targets: targets.value }
    );
  });

  const repeated = computed(() =>
    groups.value.filter((group) => group.statements.length > 1)
  );

  const byStatement = computed(() =>
    groupBy(
      groups.value.filter((group) => group.statements.length === 1),
      (group) => group.statements[0].key
    )
  );

  return { groups, repeated, byStatement };
};
